import { useState } from "react";
import { Layout } from "@/components/layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { useBusinesses } from "@/hooks/use-businesses";
import { useSubscription } from "@/hooks/use-subscription";
import { useEmailPreferences } from "@/hooks/use-email-preferences";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PlanCard } from "@/components/ui/plan-card";
import { EmailScheduler } from "@/components/settings/email-scheduler";
import { TestEmailForm } from "@/components/settings/test-email-form";
import { PeriodLockSettings } from "@/components/settings/period-lock";
import { 
  User, 
  Settings as SettingsIcon, 
  CreditCard, 
  Building, 
  Lock, 
  Bell, 
  Edit, 
  Trash, 
  Plus, 
  AlertCircle,
  Clock
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getInitials } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SubscriptionModal } from "@/components/ui/subscription-modal";
import { Link } from "wouter";

// User profile + business info schema
const profileSchema = z.object({
  fullName: z.string().optional(),
  email: z.string().email(),
  username: z.string().min(3),
  // Business fields
  phone: z.string().optional().or(z.literal("")),
  address: z.string().optional().or(z.literal("")),
  taxId: z.string().optional().or(z.literal("")),
  entityType: z.string().optional().or(z.literal("")),
});

// Password change schema
const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "New password must be at least 6 characters"),
  confirmPassword: z.string().min(6),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function SettingsPage() {
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const { businesses, activeBusiness, deleteBusiness } = useBusinesses();
  const { 
    plans, 
    currentPlan, 
    activeSubscription, 
    subscribeToNewPlan, 
    cancelSubscription, 
    isPendingSubscribe, 
    isPendingCancel 
  } = useSubscription();
  const {
    preferences: emailPreferences,
    isLoading: isLoadingPreferences,
    isUpdating: isUpdatingPreferences,
    updatePreference,
    savePreferences
  } = useEmailPreferences();
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<number | null>(null);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  
  // Form for profile settings
  const profileForm = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: user?.fullName || "",
      email: user?.email || "",
      username: user?.username || "",
      phone: (activeBusiness as any)?.phone_number || (activeBusiness as any)?.phoneNumber || "",
      address: (activeBusiness as any)?.address || "",
      taxId: (activeBusiness as any)?.tax_id || (activeBusiness as any)?.taxId || "",
      entityType: (activeBusiness as any)?.entity_type || (activeBusiness as any)?.entityType || "",
    },
  });
  
  // Form for password change
  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  
  // Handle profile update — saves both user profile and business info
  const onProfileSubmit = async (data: z.infer<typeof profileSchema>) => {
    try {
      // Update user profile
      await fetch("/api/auth/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          fullName: data.fullName,
          email: data.email,
          username: data.username,
        }),
      });

      // Update business info (phone, address, EIN, entity type)
      if (activeBusiness?.id) {
        const bizRes = await fetch(`/api/businesses/${activeBusiness.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            phone: data.phone || null,
            address: data.address || null,
            taxId: data.taxId || null,
            entityType: data.entityType || null,
          }),
        });
        if (!bizRes.ok) throw new Error("Failed to update business");
      }

      toast({
        title: "Profile updated",
        description: "Your profile information has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error?.message || "There was an error updating your profile",
        variant: "destructive",
      });
    }
  };
  
  // Handle password update
  const onPasswordSubmit = (data: z.infer<typeof passwordSchema>) => {
    // Would normally call API here
    toast({
      title: "Password updated",
      description: "Your password has been changed successfully.",
    });
    passwordForm.reset();
  };
  
  // Handle subscription change
  const handleChangePlan = (planId: number) => {
    setIsSubscriptionModalOpen(false);
    
    if (activeSubscription) {
      // Upgrade/downgrade existing subscription
      toast({
        title: "Subscription updated",
        description: "Your subscription has been updated successfully.",
      });
    } else {
      // Create new subscription
      subscribeToNewPlan(planId);
    }
  };
  
  // Handle subscription cancellation
  const handleCancelSubscription = () => {
    if (activeSubscription) {
      cancelSubscription(activeSubscription.id);
    }
  };
  
  // Handle business deletion
  const confirmDeleteBusiness = async () => {
    if (businessToDelete !== null) {
      try {
        await deleteBusiness(businessToDelete);
      } finally {
        setIsDeleteDialogOpen(false);
        setBusinessToDelete(null);
      }
    }
  };
  
  const openDeleteDialog = (businessId: number) => {
    setBusinessToDelete(businessId);
    setIsDeleteDialogOpen(true);
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-neutral-900">Settings</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Manage your account and business settings
          </p>
        </div>
        
        {/* Settings Tabs */}
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid grid-cols-3 md:w-auto w-full">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Profile</span>
            </TabsTrigger>
            <TabsTrigger value="businesses" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              <span>Businesses</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span>Billing</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Profile Settings */}
          <TabsContent value="profile">
            <div className="grid gap-6">
              {/* Account Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Account Information
                  </CardTitle>
                  <CardDescription>
                    Update your personal information and email preferences
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...profileForm}>
                    <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                      <div className="flex items-center gap-6 mb-6">
                        <Avatar className="h-20 w-20">
                          <AvatarImage src={user?.profilePicture ?? undefined} alt={user?.fullName || user?.username} />
                          <AvatarFallback className="text-lg">
                            {user?.fullName
                              ? getInitials(user.fullName)
                              : user?.username.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4 mr-2" />
                            Change Avatar
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <FormField
                          control={profileForm.control}
                          name="fullName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Full Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Your name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={profileForm.control}
                          name="username"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Username</FormLabel>
                              <FormControl>
                                <Input placeholder="username" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={profileForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem className="md:col-span-2">
                              <FormLabel>Email Address</FormLabel>
                              <FormControl>
                                <Input placeholder="email@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      {/* Business Details */}
                      {activeBusiness && (
                        <>
                          <div className="pt-4 border-t">
                            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center">
                              <Building className="h-4 w-4 mr-2" />
                              Business Information
                            </h3>
                          </div>

                          <div className="grid gap-4 md:grid-cols-2">
                            <FormField
                              control={profileForm.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Phone Number</FormLabel>
                                  <FormControl>
                                    <Input placeholder="(555) 123-4567" {...field} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={profileForm.control}
                              name="taxId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>EIN / Tax ID</FormLabel>
                                  <FormControl>
                                    <Input placeholder="XX-XXXXXXX" {...field} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={profileForm.control}
                            name="address"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Business Address</FormLabel>
                                <FormControl>
                                  <Input placeholder="123 Main St, Sacramento, CA 95814" {...field} />
                                </FormControl>
                                <FormDescription>Used on invoices and official documents</FormDescription>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={profileForm.control}
                            name="entityType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Entity Type</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value || ""}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select entity type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                                    <SelectItem value="llc">LLC</SelectItem>
                                    <SelectItem value="s_corp">S-Corporation</SelectItem>
                                    <SelectItem value="c_corp">C-Corporation</SelectItem>
                                    <SelectItem value="partnership">Partnership</SelectItem>
                                    <SelectItem value="nonprofit">Nonprofit</SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                        </>
                      )}

                      <div className="flex items-center space-x-2">
                        <Switch id="emailNotifications" defaultChecked />
                        <Label htmlFor="emailNotifications">
                          Receive email notifications and updates
                        </Label>
                      </div>

                      <Button type="submit">Save Profile Changes</Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
              
              {/* Password and Security */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Lock className="h-5 w-5 mr-2" />
                    Password & Security
                  </CardTitle>
                  <CardDescription>
                    Change your password and manage security settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...passwordForm}>
                    <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                      <FormField
                        control={passwordForm.control}
                        name="currentPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Current Password</FormLabel>
                            <FormControl>
                              <Input type="password" placeholder="••••••••" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <FormField
                          control={passwordForm.control}
                          name="newPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>New Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="••••••••" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={passwordForm.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Confirm New Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="••••••••" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <Button type="submit">Update Password</Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
              
              {/* Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Bell className="h-5 w-5 mr-2" />
                    Notification Preferences
                  </CardTitle>
                  <CardDescription>
                    Control how and when you receive notifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingPreferences ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="animate-pulse h-6 w-48 bg-gray-200 rounded"></div>
                        <div className="animate-pulse h-5 w-10 bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="animate-pulse h-6 w-48 bg-gray-200 rounded"></div>
                        <div className="animate-pulse h-5 w-10 bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="animate-pulse h-6 w-48 bg-gray-200 rounded"></div>
                        <div className="animate-pulse h-5 w-10 bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="animate-pulse h-6 w-48 bg-gray-200 rounded"></div>
                        <div className="animate-pulse h-5 w-10 bg-gray-200 rounded-full"></div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Report Summaries</h4>
                          <p className="text-sm text-neutral-500">
                            Receive regular summaries of your financial reports
                          </p>
                        </div>
                        <Switch 
                          id="reportsSummary" 
                          checked={emailPreferences.reportsSummary} 
                          onCheckedChange={(checked) => updatePreference('reportsSummary', checked)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Task Reminders</h4>
                          <p className="text-sm text-neutral-500">
                            Get reminders about upcoming and overdue tasks
                          </p>
                        </div>
                        <Switch 
                          id="taskReminders" 
                          checked={emailPreferences.taskReminders} 
                          onCheckedChange={(checked) => updatePreference('taskReminders', checked)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Subscription Alerts</h4>
                          <p className="text-sm text-neutral-500">
                            Be notified about subscription renewals and changes
                          </p>
                        </div>
                        <Switch 
                          id="subscriptionAlerts" 
                          checked={emailPreferences.subscriptionAlerts} 
                          onCheckedChange={(checked) => updatePreference('subscriptionAlerts', checked)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Marketing Emails</h4>
                          <p className="text-sm text-neutral-500">
                            Receive news, updates, and special offers
                          </p>
                        </div>
                        <Switch 
                          id="marketingEmails" 
                          checked={emailPreferences.marketingEmails} 
                          onCheckedChange={(checked) => updatePreference('marketingEmails', checked)}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
                <CardFooter>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={savePreferences}
                    disabled={isLoadingPreferences || isUpdatingPreferences}
                  >
                    {isUpdatingPreferences ? (
                      <>
                        <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-neutral-900 border-t-transparent"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Email Preferences'
                    )}
                  </Button>
                </CardFooter>
              </Card>
              
              {/* Email Scheduler */}
              <EmailScheduler />
              
              {/* Test Email Form */}
              <TestEmailForm />

              {/* Period Lock */}
              <PeriodLockSettings />
            </div>
          </TabsContent>

          {/* Businesses Settings */}
          <TabsContent value="businesses">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center">
                      <Building className="h-5 w-5 mr-2" />
                      Your Businesses
                    </CardTitle>
                    <Button asChild>
                      <Link href="/business/create">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Business
                      </Link>
                    </Button>
                  </div>
                  <CardDescription>
                    Manage the businesses linked to your account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {businesses.length === 0 ? (
                    <div className="text-center py-8">
                      <Building className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
                      <h3 className="text-lg font-medium mb-2">No businesses yet</h3>
                      <p className="text-neutral-500 mb-4">Create your first business to get started</p>
                      <Button asChild>
                        <Link href="/business/create">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Business
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <ul className="divide-y">
                      {Array.isArray(businesses) && businesses.map((business) => (
                        <li key={business.id} className="py-4 first:pt-0 last:pb-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold">
                                {getInitials(business.name)}
                              </div>
                              <div className="ml-3">
                                <h4 className="font-medium">{business.name}</h4>
                                <p className="text-sm text-neutral-500">
                                  {activeSubscription
                                    ? `${activeSubscription.isTrial ? "Free Trial" : (activeSubscription.planName || "Active")} Plan`
                                    : "No active subscription"}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button variant="outline" size="sm" asChild>
                                <Link href={`/business/${business.id}/edit`}>
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Link>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <Link href={`/settings/timezone/${business.id}`}>
                                  <Clock className="h-4 w-4 mr-1" />
                                  Timezone
                                </Link>
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => openDeleteDialog(business.id)}
                              >
                                <Trash className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              
              {/* Import/Export Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <SettingsIcon className="h-5 w-5 mr-2" />
                    Import/Export Data
                  </CardTitle>
                  <CardDescription>
                    Transfer data between systems or make backups
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card className="bg-neutral-50 border-dashed">
                      <CardHeader className="py-4">
                        <CardTitle className="text-base">Import Data</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-4">
                        <p className="text-sm text-neutral-500 mb-4">
                          Import transactions, categories, and other data from external sources
                        </p>
                        <Button variant="outline" className="w-full">
                          Import Data
                        </Button>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-neutral-50 border-dashed">
                      <CardHeader className="py-4">
                        <CardTitle className="text-base">Export Data</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-4">
                        <p className="text-sm text-neutral-500 mb-4">
                          Export your data for backup or to use in other systems
                        </p>
                        <Button variant="outline" className="w-full">
                          Export Data
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
              
              {/* Danger Zone */}
              <Card className="border-destructive/30">
                <CardHeader className="border-b border-destructive/30">
                  <CardTitle className="text-destructive flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Danger Zone
                  </CardTitle>
                  <CardDescription>
                    Irreversible actions that affect your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium mb-1">Delete All Data</h4>
                      <p className="text-sm text-neutral-500 mb-3">
                        This will permanently delete all your transactions, reports, and settings
                      </p>
                      <Button variant="destructive">Delete All Data</Button>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-1">Delete Account</h4>
                      <p className="text-sm text-neutral-500 mb-3">
                        This will permanently delete your account and all associated data
                      </p>
                      <Button variant="destructive" onClick={logout}>Delete Account</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Billing Settings */}
          <TabsContent value="billing">
            <div className="space-y-6">
              {activeBusiness ? (
                <>
                  {/* Current Subscription */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <CreditCard className="h-5 w-5 mr-2" />
                        Current Subscription
                      </CardTitle>
                      <CardDescription>
                        {activeBusiness.name}'s subscription details
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-neutral-50 rounded-lg p-4 mb-6">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="font-medium">{currentPlan?.name || "Free Plan"}</h3>
                            <p className="text-sm text-neutral-500">
                              {activeSubscription 
                                ? `Active since ${new Date(activeSubscription.startDate).toLocaleDateString()}`
                                : "No active subscription"}
                            </p>
                          </div>
                          {currentPlan && (
                            <div className="text-xl font-bold">${currentPlan.price}/mo</div>
                          )}
                        </div>
                        
                        {currentPlan && (
                          <div className="space-y-2 mb-4">
                            <h4 className="text-sm font-medium">Plan Features:</h4>
                            <ul className="space-y-1">
                              {currentPlan.features.map((feature, index) => (
                                <li key={index} className="text-sm flex items-center">
                                  <span className="text-primary mr-2">✓</span> {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row gap-3">
                          <Button onClick={() => setIsSubscriptionModalOpen(true)}>
                            Change Plan
                          </Button>
                          {activeSubscription && (
                            <Button 
                              variant="outline" 
                              className="text-destructive hover:bg-destructive/10"
                              onClick={handleCancelSubscription}
                              disabled={isPendingCancel}
                            >
                              {isPendingCancel ? "Cancelling..." : "Cancel Subscription"}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Payment Methods */}
                      <div>
                        <h3 className="font-medium mb-3">Payment Methods</h3>
                        <div className="border rounded-md p-4 flex justify-between items-center mb-4">
                          <div className="flex items-center">
                            <div className="w-10 h-6 bg-neutral-100 rounded mr-3 flex items-center justify-center">
                              <CreditCard className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium">•••• •••• •••• 4242</p>
                              <p className="text-xs text-neutral-500">Expires 12/2025</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                        <Button variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-1" />
                          Add Payment Method
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Billing History */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Billing History</CardTitle>
                      <CardDescription>
                        Your recent invoices and payment history
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border">
                        <div className="py-3 px-4 border-b bg-neutral-50 flex justify-between">
                          <div className="font-medium">Invoice</div>
                          <div className="font-medium">Amount</div>
                        </div>
                        <div className="py-4 px-4 text-center text-neutral-500">
                          No billing history available yet
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Building className="h-12 w-12 text-neutral-300 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Business Selected</h3>
                    <p className="text-neutral-500 mb-6 text-center max-w-md">
                      Please select a business to view and manage subscription information
                    </p>
                    <Button asChild>
                      <Link href="/business/create">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Business
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              {/* Available Plans */}
              <Card>
                <CardHeader>
                  <CardTitle>Available Plans</CardTitle>
                  <CardDescription>
                    Choose the right plan for your business needs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan, index) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        currentPlanId={currentPlan?.id}
                        onSelect={handleChangePlan}
                        isPending={isPendingSubscribe}
                        featured={index === 1} // Mark the middle plan as featured
                        disabled={!activeBusiness}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Delete Business Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete this business
                and all of its data including transactions, reports, and settings.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteBusiness}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete Business
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Subscription Modal */}
        <SubscriptionModal
          open={isSubscriptionModalOpen}
          onOpenChange={setIsSubscriptionModalOpen}
          onSubscribe={handleChangePlan}
          onCancel={() => setIsSubscriptionModalOpen(false)}
        />
      </div>
    </Layout>
  );
}
